import { useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../useRecruitmentUsers';
import {
  emptyJobPostingDraft,
  isJobPostingDraftComplete,
  sampleJobPostingDraft,
  toJobPostingRequest,
  type JobPostingDraft,
} from '../utils/jobPostingDraft';
import { vacanciesApi } from '../vacanciesApi';
import type { VacancyListItem } from '../vacancyTypes';
import { JobPostingFields } from './JobPostingFields';

/**
 * Tags the SELECTED rows for job posting — the selection bar's entry. A selection can span several
 * positions and `<TagForJobPostingModal>` holds one, so this form skips the picking and asks only
 * the Job Posting stage (19 Oct 2026), once, for every item chosen. It was a plain confirmation
 * until the stage gained fields: a click that must COLLECT is a form.
 *
 * **One request per item**, in order, so each tag lands in its own item's History Logs. A refusal
 * stops the run and keeps the form open; what landed stays landed and is said.
 */
export function TagSelectedModal({
  vacancies,
  onClose,
  onSaved,
}: {
  vacancies: VacancyListItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const currentUserId = useAuth().user?.id ?? null;
  const accounts = useRecruitmentUsers();

  const [remaining, setRemaining] = useState(vacancies);
  const [draft, setDraft] = useState<JobPostingDraft>(() => emptyJobPostingDraft(currentUserId));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = remaining.length > 0 && isJobPostingDraftComplete(draft) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = toJobPostingRequest(draft);
    const posted: string[] = [];

    try {
      for (const vacancy of remaining) {
        await vacanciesApi.tag(vacancy.id, request);
        posted.push(vacancy.id);
      }

      toast.success(`Tagged ${posted.length} item${posted.length === 1 ? '' : 's'} for job posting.`);
      onSaved();
    } catch (caught) {
      setRemaining((current) => current.filter((v) => !posted.includes(v.id)));
      const failure = caught instanceof ApiError ? caught : new ApiError('The items could not be tagged.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'tag-selected' });

      if (posted.length > 0) {
        toast.success(`Tagged ${posted.length} item${posted.length === 1 ? '' : 's'} for job posting.`);
      }
    } finally {
      setSaving(false);
    }
  }

  const n = remaining.length;

  return (
    <Modal
      open
      onClose={onClose}
      title="Tag for job posting"
      size="lg"
      id="tag-selected-modal"
      autogenerate={{ onGenerate: () => setDraft(sampleJobPostingDraft(accounts.users)), disabled: saving || accounts.loading }}
      confirm={{ id: 'tag-selected-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="tag-selected-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="tag-selected-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          {`${n} selected item${n === 1 ? '' : 's'} move${n === 1 ? 's' : ''} to For job posting until untagged or somebody is appointed to ${
            n === 1 ? 'it' : 'them'
          }. Each takes the posting details below.`}
        </p>

        <JobPostingFields
          idPrefix="tag-selected"
          draft={draft}
          onChange={setDraft}
          users={accounts.users}
          usersLoading={accounts.loading}
          usersError={accounts.error}
          disabled={saving}
          error={error}
        />
      </div>
    </Modal>
  );
}
